/**
 * useBoardNames — подписи людей доски ОО, которых нет в её списке (уволенные на датах 1С и прошлых днях; архив 1С В9, 16.10).
 * Логика — boardNames.ts. Запрос /api/staff/names идёт, только если на доске есть человек без имени (на текущих датах обычно
 * нет — запроса нет); полученные имена копятся на время жизни доски. Сбой запроса — подпись id, как раньше (доска не ломается).
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "../api/client";
import { boardPeople, missingNames, nameOfFactory, namesUrl, parseNames } from "./boardNames";

export function useBoardNames(
  known: ReadonlyMap<string, { name: string }>,
  crews: Readonly<Record<string, readonly string[]>>,
  mc: Readonly<Record<string, Readonly<Record<string, readonly string[]>>>>,
  hall: Readonly<Record<string, readonly string[]>>,
): (id: string) => string {
  const [fetched, setFetched] = useState<ReadonlyMap<string, string>>(() => new Map());
  /* спрошенные id не спрашиваем снова (и после сбоя, и неизвестные серверу) — иначе такой id крутил бы запросы */
  const [asked, setAsked] = useState<ReadonlySet<string>>(() => new Set());
  const key = useMemo(
    () => missingNames(boardPeople(crews, mc, hall), (id) => known.has(id) || fetched.has(id) || asked.has(id)).join(","),
    [crews, mc, hall, known, fetched, asked],
  );
  useEffect(() => {
    const ids = key ? key.split(",") : [];
    const url = namesUrl(ids);
    if (!url) return;
    const ac = new AbortController();
    const done = (got: Map<string, string>) => {
      if (ac.signal.aborted) return;   // день сменился — ответ не наш, недостающие спросит следующий запрос
      if (got.size) setFetched((prev) => new Map([...prev, ...got]));
      setAsked((prev) => new Set([...prev, ...ids]));
    };
    api<unknown>(url, { signal: ac.signal }).then((r) => done(parseNames(r)), () => done(new Map()));
    return () => ac.abort();
  }, [key]);
  return useMemo(() => nameOfFactory(known, fetched), [known, fetched]);
}
